import React, { useState, useContext } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Button,
  Stack,
  Box,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import { appRequest } from "routes";
import { BASE_PATH } from "utils/env";

interface AnotherConfigureProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AnotherConfigureProcessModal({
  isOpen,
  onClose,
}: AnotherConfigureProcessModalProps) {
  const { toastNotify } = useContext(AppContext);
  const [loading, setLoading] = useState(false);

  const [selectedProcessName, setSelectedProcessName] = useState("domestic");
  const [selectedProcessType, setSelectedProcessType] = useState("NonPo");

  const modalHeaderBg = useColorModeValue("white", "gray.800");
  const modalHeaderColor = useColorModeValue("gray.700", "white");
  const modalSelectBg = useColorModeValue("gray.100", "gray.700");
  const modalSelectHoverBg = useColorModeValue("gray.200", "gray.600");

  const handleApplyChanges = async () => {
    try {
      setLoading(true);

      const response: any = await appRequest(
        "workflow",
        "logNewProcess",
        {
          processName: selectedProcessName,
          processType: selectedProcessType || "NonPo",
        }
      );

      if (response?.status === "success" && response?.token) {
        sessionStorage.setItem("auth-token", response?.token);
        window.location.href = BASE_PATH;
        toastNotify({
          title: "Success",
          message: response?.data || "Process configured successfully!",
          type: "success",
        });
        setLoading(false);
        onClose();
      } else {
        setLoading(false);
        toastNotify({
          title: "Error",
          message: response?.data,
          type: "info",
        });
      }
    } catch (err: any) {
      setLoading(false);
      toastNotify({
        title: "Error",
        message: err?.message,
        type: "error",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(10px) saturate(180%)" bg="rgba(0, 0, 0, 0.15)" />
      <ModalContent
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        border="1px solid"
        borderColor={useColorModeValue("gray.100", "gray.700")}
      >
        <ModalHeader
          bg={modalHeaderBg}
          fontSize="lg"
          fontWeight="700"
          color={modalHeaderColor}
          px={6}
          pt={6}
          pb={4}
        >
          Configure Process
        </ModalHeader>
        <ModalCloseButton top={6} right={6} />

        <ModalBody px={6} py={4}>
          <Stack spacing={6}>
            <Box>
              <Text
                fontSize="11px"
                fontWeight="800"
                color="teal.500"
                mb={2}
                textTransform="uppercase"
                letterSpacing="widest"
              >
                Process Name
              </Text>
              <Select
                value={selectedProcessName}
                onChange={(e) => setSelectedProcessName(e.target.value)}
                variant="filled"
                h="45px"
                borderRadius="xl"
                bg={modalSelectBg}
                _hover={{ bg: modalSelectHoverBg }}
                _focus={{ bg: "white", borderColor: "teal.400" }}
                fontWeight="600"
              >
                <option value="sap">Sap</option>
                <option value="domestic">Domestic</option>
                <option value="emerging">Emerging</option>
                <option value="marcan">Marcan</option>
                <option value="tillomed">Tillomed</option>
                <option value="zuventus">Zuventus</option>
                <option value="mmt">MMt</option>
                <option value="pando">Pando</option>
                <option value="pr">pr</option>
                <option value="sr">sr</option>
                <option value="provakil">Provakil</option>
                <option value="foreign">foreign</option>
                <option value="crm">Marketing</option>
              </Select>
            </Box>

            <Box>
              <Text
                fontSize="11px"
                fontWeight="800"
                color="teal.500"
                mb={2}
                textTransform="uppercase"
                letterSpacing="widest"
              >
                Selection Type
              </Text>
              <Select
                value={selectedProcessType}
                onChange={(e) => setSelectedProcessType(e.target.value)}
                variant="filled"
                h="45px"
                borderRadius="xl"
                bg={modalSelectBg}
                _hover={{ bg: modalSelectHoverBg }}
                _focus={{ bg: "white", borderColor: "teal.400" }}
                fontWeight="600"
              >
                <option value="NonPo">Non PO</option>
                <option value="Po">PO</option>
              </Select>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter px={6} pb={6} pt={2}>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            borderRadius="xl"
            fontSize="sm"
            fontWeight="600"
          >
            Cancel
          </Button>

          <Button
            colorScheme="teal"
            h="45px"
            px={8}
            borderRadius="xl"
            isLoading={loading}
            onClick={handleApplyChanges}
            boxShadow="0 10px 15px -3px rgba(45, 212, 191, 0.3)"
            _hover={{ boxShadow: "0 20px 25px -5px rgba(45, 212, 191, 0.4)" }}
            fontSize="sm"
            fontWeight="700"
          >
            Apply Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
